import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, Volume2, TrendingUp, Zap, ChevronRight } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyAudioQuality - NOUVEL ÉCRAN : Qualité Audio Premium
 * Écran pédagogique premium expliquant la qualité audio Hearen
 * Position : Entre Test Calibration et Player Thérapie Sonore
 */
const JourneyAudioQuality = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [showKeyPoints, setShowKeyPoints] = useState([false, false, false, false]);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in global
    setTimeout(() => setShowContent(true), 300);
    
    // Animation cascade des points clés (stagger 100ms)
    setTimeout(() => setShowKeyPoints([true, false, false, false]), 800);
    setTimeout(() => setShowKeyPoints([true, true, false, false]), 900);
    setTimeout(() => setShowKeyPoints([true, true, true, false]), 1000);
    setTimeout(() => setShowKeyPoints([true, true, true, true]), 1100);
    
    return () => setIsFullscreen(false);
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('🎵 JourneyAudioQuality: Navigating to /journey-therapy');
    navigate('/journey-therapy');
  };

  // Points clés de la qualité audio
  const keyPoints = [
    {
      icon: Volume2,
      text: isEnglish ? 'High-quality audio' : 'Audio haute qualité',
    },
    {
      icon: TrendingUp,
      text: isEnglish ? 'Preserved dynamics' : 'Dynamique préservée',
    },
    {
      icon: Zap,
      text: isEnglish ? 'Less listening fatigue' : 'Moins de fatigue auditive',
    },
    {
      icon: Headphones,
      text: isEnglish ? 'Designed for soothing listening' : 'Pensé pour l\'écoute apaisante',
    },
  ];

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6"
      style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #3b82f6 100%)',
      }}
    >
      {/* Background glow effects */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.4) 0%, transparent 60%)',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-2xl w-full text-center"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 700ms ease-out',
        }}
      >
        {/* Icône casque avec halo pulsé */}
        <div className="flex justify-center mb-8">
          <div
            className="relative w-32 h-32 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, rgba(139, 92, 246, 0.2) 70%)',
              backdropFilter: 'blur(15px)',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              boxShadow: '0 8px 40px rgba(139, 92, 246, 0.6)',
              animation: 'audio-halo-pulse 3s ease-in-out infinite',
            }}
          >
            <Headphones className="w-16 h-16 text-white" strokeWidth={1.5} />
          </div>
        </div>

        {/* Titre principal */}
        <h1 className="text-4xl font-bold text-white mb-6 tracking-wide">
          {isEnglish 
            ? 'Premium sound quality'
            : 'Qualité sonore premium'
          }
        </h1>

        {/* Texte principal explicatif */}
        <p className="text-white/90 text-lg leading-relaxed mb-12 max-w-xl mx-auto">
          {isEnglish
            ? 'Hearen uses high-quality audio to preserve every nuance and support deep calm, with less listening fatigue.'
            : 'Hearen utilise un audio haute qualité pour préserver chaque nuance et favoriser un apaisement profond, sans fatigue auditive.'
          }
        </p>

        {/* Points clés - Grid 2x2 avec fade-in cascade */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 max-w-2xl mx-auto">
          {keyPoints.map((point, index) => (
            <div
              key={index}
              className="flex items-center gap-3 px-6 py-4 rounded-2xl text-left"
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                opacity: showKeyPoints[index] ? 1 : 0,
                transform: showKeyPoints[index] ? 'translateY(0)' : 'translateY(10px)',
                transition: `all 400ms ease-out ${100 * index}ms`,
              }}
            >
              <point.icon className="w-6 h-6 text-white/90 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-white/90 text-sm font-medium">{point.text}</span>
            </div>
          ))}
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-indigo-900"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              boxShadow: '0 8px 30px rgba(255, 255, 255, 0.4)',
            }}
          >
            {isEnglish ? 'Discover Sound' : 'Découvrir le Son'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes audio-halo-pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.9;
            box-shadow: 0 8px 40px rgba(139, 92, 246, 0.6);
          }
          50% { 
            transform: scale(1.05);
            opacity: 1;
            box-shadow: 0 12px 60px rgba(139, 92, 246, 0.8);
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyAudioQuality;
